import type {
  Organization,
  ProductType,
} from "@frontend/lib/graphql/generated-gql";
import type { Component } from "./[clientSlug]/types";
export interface Product {
  id: number;
  name: string;
  product_type?: ProductType;
  productToHrt: Array<{ hrt: { isConsumerFacing: boolean } }>;
  displayMetadata?: {
    description: Array<{ value: string }>;
    preDiscount?: string;
    imageUrl?: string;
  };
  popularity: number;
  ProductTranslation: Array<{
    languageCode: string;
    name: string;
    displayMetadata?: unknown;
    renderConfig?: {
      Components?: Array<Component>;
    };
  }>;
}

export interface ProductWithTranslations {
  name: string;
  ProductTranslation?: Array<{
    languageCode: string;
    name: string;
    displayMetadata?: unknown;
  }>;
}

interface ProductWithDescription {
  displayMetadata?: {
    description?: Array<{ value: string }>;
    kiosk?: {
      title?: string;
      description?: string[];
    };
  };
  ProductTranslation?: Array<{
    languageCode: string;
    displayMetadata?: unknown;
  }>;
}

interface ProductCategoryWithTranslations {
  name: string;
  ProductCategoryTranslation?: Array<{
    languageCode: string;
    name?: string;
  }>;
}

/**
 * Gets the translated product name for the given language code.
 * Falls back to the original product name if no translation is available.
 */
export function getTranslatedProductName(
  product: ProductWithTranslations,
  languageCode: string,
): string {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  return translation?.name || product.name;
}

/**
 * Gets the translated product description for the given language code.
 * Falls back to the original product description if no translation is available.
 */
export function getTranslatedProductDescription(
  product: ProductWithDescription,
  languageCode: string,
): string {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  const translatedDescription = translation?.displayMetadata as
    | { description?: Array<{ value: string }> }
    | undefined;
  return (
    translatedDescription?.description?.[0]?.value ??
    product.displayMetadata?.description?.[0]?.value ??
    ""
  );
}

interface ProductWithLearnMoreButtonText {
  displayMetadata?: unknown;
  ProductTranslation?: Array<{
    languageCode: string;
    displayMetadata?: unknown;
  }>;
}

function readLearnMoreButtonText(displayMetadata: unknown): string | undefined {
  const value = (displayMetadata as { learnMoreButtonText?: unknown } | null)
    ?.learnMoreButtonText;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Gets the product page "learn more" button label for the given language code
 * from `displayMetadata.learnMoreButtonText`. Falls back to the base product
 * metadata, then to undefined. Blank values count as not set.
 */
export function getTranslatedLearnMoreButtonText(
  product: ProductWithLearnMoreButtonText,
  languageCode: string,
): string | undefined {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  return (
    readLearnMoreButtonText(translation?.displayMetadata) ??
    readLearnMoreButtonText(product.displayMetadata)
  );
}

/**
 * Gets the translated product kiosk title for the given language code.
 * Falls back to the original product kiosk title if no translation is available.
 */
export function getTranslatedProductKioskTitle(
  product: ProductWithDescription,
  languageCode: string,
): string {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  const translatedMetadata = translation?.displayMetadata as
    | { kiosk?: { title?: string } }
    | undefined;
  const displayMetadata = product.displayMetadata as
    | { kiosk?: { title?: string } }
    | undefined;
  return (
    translatedMetadata?.kiosk?.title || displayMetadata?.kiosk?.title || ""
  );
}

/**
 * Gets the translated product kiosk description for the given language code.
 * Falls back to the original product kiosk description if no translation is available.
 */
export function getTranslatedProductKioskDescription(
  product: ProductWithDescription,
  languageCode: string,
): string[] {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  const translatedMetadata = translation?.displayMetadata as
    | { kiosk?: { description?: string[] } }
    | undefined;
  const displayMetadata = product.displayMetadata as
    | { kiosk?: { description?: string[] } }
    | undefined;
  return (
    translatedMetadata?.kiosk?.description ||
    displayMetadata?.kiosk?.description ||
    []
  );
}

/**
 * Gets the translated product category name for the given language code.
 * Falls back to the original product category name if no translation is available.
 */
export function getTranslatedProductCategoryName(
  category: ProductCategoryWithTranslations,
  languageCode: string,
): string {
  const translation = category.ProductCategoryTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  return translation?.name || category.name;
}

/** One entry of an HRT's `displayMetadata.description`. */
export interface HrtDescriptionEntry {
  value: string;
  type?: string;
  /** Optional external URL; `value` renders as the link text. */
  link?: string;
}

interface HrtWithTranslations {
  name: string;
  displayName?: string | null;
  displayMetadata?: {
    description?: Array<HrtDescriptionEntry>;
    ingredients?: Array<{ value: string }>;
  };
  HrtTranslation?: Array<{
    languageCode: string;
    name?: string;
    displayName?: string | null;
    displayMetadata?: {
      description?: Array<HrtDescriptionEntry>;
      ingredients?: Array<{ value: string }>;
    };
  }>;
}

/**
 * Gets the translated HRT name for the given language code.
 * Falls back to the original HRT name if no translation is available.
 * Returns an empty string if the HRT data is invalid or missing.
 */
export function getTranslatedHrtName(
  hrt: Partial<HrtWithTranslations>,
  languageCode: string,
): string {
  // Handle empty/undefined input gracefully
  if (!hrt || (!hrt.name && !hrt.HrtTranslation?.length)) {
    return "";
  }

  const translation = hrt.HrtTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  // Use displayName from translation, fallback to original name, or empty string
  return translation?.displayName ?? hrt.displayName ?? hrt.name ?? "";
}

/**
 * Gets the translated HRT displayMetadata for the given language code.
 * Falls back to the original displayMetadata if no translation is available.
 */
export function getTranslatedHrtDisplayMetadata(
  hrt: HrtWithTranslations,
  languageCode: string,
): {
  description?: Array<HrtDescriptionEntry>;
  ingredients?: Array<{ value: string }>;
} {
  const translation = hrt.HrtTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  const translatedMetadata = translation?.displayMetadata as
    | {
        description?: Array<HrtDescriptionEntry>;
        ingredients?: Array<{ value: string }>;
      }
    | undefined;
  return {
    description:
      translatedMetadata?.description ?? hrt.displayMetadata?.description,
    ingredients:
      translatedMetadata?.ingredients ?? hrt.displayMetadata?.ingredients,
  };
}

export function getTranslatedComponents(
  product: Product,
  languageCode: string,
): Array<Component> | undefined {
  const translation = product.ProductTranslation?.find(
    (t) => t.languageCode === languageCode,
  );
  return translation?.renderConfig?.Components;
}

export function getTranslatedOrgComponents(
  org: Organization,
  languageCode: string,
): Array<Component> | undefined {
  const translation = org.organizationTranslations?.find(
    (t) => t.languageCode === languageCode,
  );
  return translation?.renderConfig?.Components;
}
